import { Link } from '@tanstack/react-router'
import { PURCHASE_ORDER_STATUS_VARIANT } from '../lib/purchase-order-lines'
import type { ColumnDef } from '@tanstack/react-table'
import type { PurchaseOrder } from '@/api/endpoints/purchase-orders'
import { Badge } from '@/components/ui/badge'
import {
  formatIsoDate,
  formatTimestamp,
} from '@/features/inventory-deliveries/components/delivery-columns'

type Translate = (key: string, options?: Record<string, unknown>) => string

/** The status badge, plus "Status unklar" for a send that never confirmed. */
export function PurchaseOrderStatusBadges({
  order,
  t,
}: {
  order: Pick<PurchaseOrder, 'status' | 'statusUnclear'>
  t: Translate
}) {
  return (
    <div className="flex flex-wrap items-center gap-1">
      <Badge variant={PURCHASE_ORDER_STATUS_VARIANT[order.status]}>
        {t(`status.${order.status}`)}
      </Badge>
      {order.statusUnclear && (
        <Badge
          variant="outline"
          className="border-warning/50 bg-warning/10 text-warning-foreground"
        >
          {t('status.unclear')}
        </Badge>
      )}
    </div>
  )
}

/** The list pages server-side and takes no sort parameter, so no column sorts. */
export function buildPurchaseOrderColumns(
  t: Translate,
  options: { compact?: boolean } = {},
): Array<ColumnDef<PurchaseOrder>> {
  const columns: Array<ColumnDef<PurchaseOrder>> = [
    {
      accessorKey: 'orderNumber',
      header: t('list.column.number'),
      size: 120,
      enableSorting: false,
      cell: ({ row }) => (
        <span className="font-mono text-sm">
          {row.original.orderNumber ?? '–'}
        </span>
      ),
      meta: { label: t('list.column.number') },
    },
    {
      accessorKey: 'deliveryDate',
      header: t('list.column.deliveryDate'),
      size: 120,
      enableSorting: false,
      cell: ({ row }) => (
        <span className="tabular-nums">
          {formatIsoDate(row.original.deliveryDate)}
        </span>
      ),
      meta: { label: t('list.column.deliveryDate') },
    },
    {
      id: 'status',
      header: t('list.column.status'),
      size: 170,
      enableSorting: false,
      cell: ({ row }) => (
        <PurchaseOrderStatusBadges order={row.original} t={t} />
      ),
      meta: { label: t('list.column.status') },
    },
    {
      accessorKey: 'lineCount',
      header: t('list.column.lines'),
      size: 100,
      enableSorting: false,
      cell: ({ row }) => (
        <span className="block text-right tabular-nums">
          {row.original.lineCount}
        </span>
      ),
      meta: { label: t('list.column.lines') },
    },
    {
      accessorKey: 'sentBy',
      header: t('list.column.sentBy'),
      size: 160,
      enableSorting: false,
      cell: ({ row }) => (
        <span className="text-muted-foreground">
          {row.original.sentBy ?? '–'}
        </span>
      ),
      meta: { label: t('list.column.sentBy') },
    },
    {
      accessorKey: 'sentAt',
      header: t('list.column.sentAt'),
      size: 170,
      enableSorting: false,
      cell: ({ row }) => (
        <span className="text-muted-foreground tabular-nums">
          {formatTimestamp(row.original.sentAt)}
        </span>
      ),
      meta: { label: t('list.column.sentAt') },
    },
    {
      accessorKey: 'deliveryId',
      header: t('list.column.delivery'),
      size: 110,
      enableSorting: false,
      cell: ({ row }) =>
        row.original.deliveryId ? (
          <Link
            to="/inventory/deliveries/$id"
            params={{ id: row.original.deliveryId }}
            search={{ restaurantId: row.original.restaurantId }}
            className="underline"
            // The row itself opens the order; the link opens the delivery.
            onClick={(e) => e.stopPropagation()}
          >
            {t('list.openDelivery')}
          </Link>
        ) : (
          <span className="text-muted-foreground">–</span>
        ),
      meta: { label: t('list.column.delivery') },
    },
  ]

  if (!options.compact) return columns
  // Phone: number, day and status find the right order.
  const dropped = new Set(['lineCount', 'sentBy', 'sentAt', 'deliveryId'])
  return columns.filter(
    (c) => !dropped.has((c as { accessorKey?: string }).accessorKey ?? c.id!),
  )
}
